import type { ClientDto } from '@fos/shared/contracts/crm';
import {
  BookOpen,
  Calendar,
  FileText,
  FolderOpen,
  History,
  ListTodo,
  type LucideIcon,
  Mail,
  Server,
  Timer,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { EmptyState } from '../../ui/States';

/**
 * Miejsca na treści innych modułów w profilu klienta (/klienci/:id). Wypełnia je kompozycja Fali 1/2 albo moduły
 * (zadania, czas, utrzymanie, poczta, spotkania, wiedza, historia) bez zmian w module klientów.
 *
 *  - zakładki: `setClientTab('tasks', ({ client }) => <TaskList clientId={client.id} />)`,
 *  - sekcje Przeglądu: `registerClientOverviewSection({ key: 'tasks', column: 'right', order: 10, component })`
 *    (kolumny jak w Figmie: `top` — baner i kafelki, `left` — projekty, usługi, serwer, „W skrócie”,
 *    `right` — otwarte zadania; moduł klientów dodaje sekcję „Kontakty” w `right`, order 100),
 *  - „Nowe zadanie”: `setClientNewTaskDialog(({ client, open, onOpenChange }) => <NewTaskDialog … />)`;
 *    bez rejestracji przycisk prowadzi do `/zadania?nowe=1&klient=<id>`,
 *  - „Dodaj czas”: `setClientAddTimeDialog(({ client, open, onOpenChange }) => <AddTimeDialog … />)` (kompozycja).
 */

export type ClientTabKey =
  | 'overview'
  | 'tasks'
  | 'time'
  | 'maintenance'
  | 'messages'
  | 'meetings'
  | 'documents'
  | 'knowledge'
  | 'offers'
  | 'history';
export type ClientTabComponent = ComponentType<{ client: ClientDto }>;

/** Zakładki profilu w kolejności z Figmy. Adres: `/klienci/:id?zakladka=<slug>`. */
export const CLIENT_TABS: readonly { key: ClientTabKey; slug: string; label: string }[] = [
  { key: 'overview', slug: 'przeglad', label: 'Przegląd' },
  { key: 'tasks', slug: 'zadania', label: 'Zadania' },
  { key: 'time', slug: 'czas', label: 'Czas' },
  { key: 'maintenance', slug: 'utrzymanie', label: 'Utrzymanie' },
  { key: 'messages', slug: 'wiadomosci', label: 'Wiadomości' },
  { key: 'meetings', slug: 'spotkania', label: 'Spotkania' },
  // 17.10.2026 (DECISIONS „Dokumenty klienta”): pliki, linki i ustalenia — treść z modułu klientów (ClientOverview.tsx)
  { key: 'documents', slug: 'dokumenty', label: 'Dokumenty' },
  { key: 'knowledge', slug: 'wiedza', label: 'Wiedza' },
  { key: 'offers', slug: 'oferty', label: 'Oferty' },
  { key: 'history', slug: 'historia', label: 'Historia' },
];

const PLACEHOLDERS: Record<
  Exclude<ClientTabKey, 'overview'>,
  { icon: LucideIcon; title: string; description: string }
> = {
  tasks: {
    icon: ListTodo,
    title: 'Zadania klienta',
    description: 'Tu pojawią się otwarte i zakończone zadania tego klienta.',
  },
  time: {
    icon: Timer,
    title: 'Czas klienta',
    description: 'Tu zobaczysz wpisy czasu, godziny w pakiecie i kwotę do rozliczenia.',
  },
  maintenance: {
    icon: Server,
    title: 'Utrzymanie',
    description: 'Tu pojawią się strony, domeny i umowy utrzymania tego klienta.',
  },
  messages: {
    icon: Mail,
    title: 'Wiadomości',
    description: 'Tu pojawi się korespondencja z klientem po podłączeniu poczty.',
  },
  meetings: {
    icon: Calendar,
    title: 'Spotkania',
    description: 'Tu pojawią się spotkania z klientem i notatki z nich.',
  },
  documents: {
    icon: FolderOpen,
    title: 'Dokumenty',
    description: 'Tu pojawią się umowy, pliki, linki i ustalenia z klientem.',
  },
  knowledge: {
    icon: BookOpen,
    title: 'Wiedza',
    description: 'Tu pojawią się notatki, decyzje i pliki dotyczące klienta.',
  },
  offers: {
    icon: FileText,
    title: 'Oferty',
    description: 'Tu pojawią się oferty dla klienta z historią wersji.',
  },
  history: {
    icon: History,
    title: 'Historia zmian',
    description: 'Tu zobaczysz, kto i kiedy zmienił dane klienta.',
  },
};

const tabs: Partial<Record<ClientTabKey, ClientTabComponent>> = {};

/** Rejestruje treść zakładki profilu klienta (null = przywraca domyślną). */
export function setClientTab(key: ClientTabKey, component: ClientTabComponent | null): void {
  if (component) tabs[key] = component;
  else delete tabs[key];
}

export function getClientTab(key: ClientTabKey): ClientTabComponent | undefined {
  return tabs[key];
}

/** Treść zakładki: zarejestrowana albo spokojny placeholder „pojawi się”. */
export function ClientTabPlaceholder({ tab }: { tab: Exclude<ClientTabKey, 'overview'> }) {
  const p = PLACEHOLDERS[tab];
  return (
    <div className="rounded-xl border border-dashed border-line">
      <EmptyState icon={p.icon} title={p.title} description={p.description} />
    </div>
  );
}

// ── Sekcje Przeglądu ────────────────────────────────────────────────────────
export interface ClientOverviewSection {
  key: string;
  /** `top` (pełna szerokość: baner, kafelki), `left` i `right` (dwie kolumny na desktopie) */
  column: 'top' | 'left' | 'right';
  /** kolejność w kolumnie (rosnąco) */
  order: number;
  component: ClientTabComponent;
}

const overviewSections = new Map<string, ClientOverviewSection>();

/** Dodaje albo podmienia sekcję Przeglądu (po kluczu). */
export function registerClientOverviewSection(section: ClientOverviewSection): void {
  overviewSections.set(section.key, section);
}

export function unregisterClientOverviewSection(key: string): void {
  overviewSections.delete(key);
}

export function getClientOverviewSections(column: ClientOverviewSection['column']): ClientOverviewSection[] {
  return [...overviewSections.values()].filter((s) => s.column === column).sort((a, b) => a.order - b.order);
}

// ── „Nowe zadanie” ──────────────────────────────────────────────────────────
export type ClientNewTaskDialog = ComponentType<{
  client: ClientDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>;

let newTaskDialog: ClientNewTaskDialog | null = null;

/** Dialog „Nowe zadanie” z modułu zadań (bez rejestracji przycisk prowadzi do `/zadania?nowe=1&klient=<id>`). */
export function setClientNewTaskDialog(component: ClientNewTaskDialog | null): void {
  newTaskDialog = component;
}

export function getClientNewTaskDialog(): ClientNewTaskDialog | null {
  return newTaskDialog;
}

// ── „Dodaj czas” ────────────────────────────────────────────────────────────
let addTimeDialog: ClientNewTaskDialog | null = null;

/** Dialog „Dodaj czas” z modułu Czas z klientem profilu (bez rejestracji przycisk prowadzi do `/czas?nowy=1`). */
export function setClientAddTimeDialog(component: ClientNewTaskDialog | null): void {
  addTimeDialog = component;
}

export function getClientAddTimeDialog(): ClientNewTaskDialog | null {
  return addTimeDialog;
}
